/**
 * The demo tour's content: which pages it visits and what it says on each.
 *
 * Pure on purpose (no React, no imports) so `node --test` can check it. The
 * React side (`useDemoTour.js`) drops steps whose page the viewer cannot open,
 * and `tourRunner.js` drives driver.js through what is left.
 *
 * Copy is written for a bursar or head teacher seeing the product for the
 * first time: say what the screen shows and why it matters to them, in one or
 * two short sentences, with no jargon in the titles.
 */

/**
 * Every page the tour visits: the `routeConfig.jsx` route id and its href.
 * A render test asserts these still match the router, so a renamed route
 * fails a test rather than silently skipping its tour steps.
 */
export const TOUR_ROUTES = [
    { id: 'power-overview', path: '/power' },
    { id: 'power-timeline', path: '/power/timeline' },
    { id: 'power-holidays', path: '/power/holidays' },
    { id: 'alerts-overview', path: '/alerts' },
    { id: 'power-carbon-report', path: '/power/carbon-report' },
    { id: 'power-targets', path: '/power/targets' }
]

export const TOUR_START_PATH = '/power'

const pathFor = (routeId) => {
    const route = TOUR_ROUTES.find((r) => r.id === routeId)
    if (!route) throw new Error(`Unknown tour route: ${routeId}`)
    return route.path
}

const anchor = (name) => `[data-tour="${name}"]`

// What auto-start waits for before opening the welcome. The period tabs, not
// the first step's stats grid: the grid is absent when a period has no
// readings, and waiting on it made the tour silently never start.
export const TOUR_READY_SELECTOR = anchor('overview-period')

/**
 * The ordered tour steps for one org.
 *
 * Each step: `{ id, routeId, route, selector, title, description, side,
 * align }`. A step with no `selector` is shown as a centred card. `showCost`
 * is false for viewers who cannot see £ figures, so the headline step does
 * not promise them a number that isn't on their screen.
 */
export function buildTourSteps({ orgName, showCost = true } = {}) {
    const school = orgName?.trim() || 'this school'
    const overview = 'power-overview'

    return [
        {
            id: 'welcome',
            routeId: overview,
            route: pathFor(overview),
            selector: null,
            title: `Welcome to ${school}`,
            description:
                'This is a demo school with three years of realistic half-hourly meter data. The tour takes about two minutes, and you can leave it at any point.'
        },
        {
            id: 'overview-stats',
            routeId: overview,
            route: pathFor(overview),
            selector: anchor('overview-stats'),
            title: 'The headline numbers',
            description: showCost
                ? 'How much electricity and gas the school used over the period, what it cost, and what that means in carbon.'
                : 'How much electricity and gas the school used over the period, and what that means in carbon.',
            side: 'bottom',
            align: 'start'
        },
        {
            id: 'overview-period',
            routeId: overview,
            route: pathFor(overview),
            selector: anchor('overview-period'),
            title: 'Pick the time window',
            description:
                'Switch between yesterday, the last 7 days, the last 30 days and the last year. Everything on this page updates together.',
            side: 'bottom',
            align: 'start'
        },
        {
            id: 'overview-mix',
            routeId: overview,
            route: pathFor(overview),
            selector: anchor('overview-mix'),
            title: 'Where your energy goes',
            description:
                'Electricity and gas across the whole site over the past year, and how your total splits between the two.',
            side: 'top',
            align: 'start'
        },
        {
            id: 'overview-targets',
            routeId: overview,
            route: pathFor(overview),
            selector: anchor('overview-targets'),
            title: 'A target everyone can see',
            description:
                'Set a reduction goal, such as 5% less electricity this year, and progress shows here for staff and governors alike.',
            side: 'top',
            align: 'start'
        },
        {
            id: 'overview-whatif',
            routeId: overview,
            route: pathFor(overview),
            selector: anchor('overview-whatif'),
            title: showCost
                ? 'Try a change before you fund it'
                : 'Try a change before you make it',
            description: showCost
                ? 'Move the slider to see what a cut in use would save, before you commit any budget to making it happen.'
                : 'Move the slider to see how much energy and carbon a cut in use would save.',
            side: 'top',
            align: 'start'
        },
        {
            id: 'overview-equivalents',
            routeId: overview,
            route: pathFor(overview),
            selector: anchor('overview-equivalents'),
            title: 'Numbers people can picture',
            description:
                'Kilowatt-hours turned into everyday comparisons, ready for an assembly, a newsletter or a governors’ meeting.',
            side: 'top',
            align: 'start'
        },
        {
            id: 'timeline',
            routeId: 'power-timeline',
            route: pathFor('power-timeline'),
            selector: anchor('power-timeline'),
            title: 'The shape of a day',
            description:
                'Play back the school’s use half-hour by half-hour. What runs overnight and at weekends, when the buildings are empty, is usually the easiest saving.',
            side: 'top',
            align: 'center'
        },
        {
            id: 'holidays',
            routeId: 'power-holidays',
            route: pathFor('power-holidays'),
            selector: anchor('power-holidays'),
            title: showCost
                ? 'What the empty school cost'
                : 'What the empty school used',
            description:
                'Each holiday closure, what was used while nobody was in, and how that compares with what it should have been.',
            side: 'top',
            align: 'start'
        },
        {
            id: 'alerts',
            routeId: 'alerts-overview',
            route: pathFor('alerts-overview'),
            selector: anchor('alerts-overview'),
            title: 'Problems found for you',
            description: showCost
                ? 'Nightly checks spot creeping overnight use and budget overruns, and tell the people who can act on them.'
                : 'Nightly checks spot creeping overnight use and sudden spikes, and tell the people who can act on them.',
            side: 'top',
            align: 'start'
        },
        {
            id: 'carbon-report',
            routeId: 'power-carbon-report',
            route: pathFor('power-carbon-report'),
            selector: anchor('power-carbon-report'),
            title: 'The annual carbon report',
            description:
                'A year’s emissions with a comparison to the year before, ready to download for governors or your annual report.',
            side: 'top',
            align: 'start'
        },
        {
            id: 'targets',
            routeId: 'power-targets',
            route: pathFor('power-targets'),
            selector: anchor('power-targets'),
            title: 'Progress against last year',
            description:
                'Each target tracked week by week against the same period last year, so you know early whether you are on course.',
            side: 'top',
            align: 'start'
        },
        {
            id: 'page-concept',
            routeId: overview,
            route: pathFor(overview),
            selector: anchor('page-concept'),
            title: 'Help on every page',
            description:
                'This ? button explains the page you are on in plain English: what it shows and how to read it.',
            side: 'bottom',
            align: 'start'
        },
        {
            id: 'done',
            routeId: overview,
            route: pathFor(overview),
            selector: null,
            title: 'Over to you',
            description:
                'Explore freely. Nothing you do here affects a real school. You can replay this tour any time from the top bar.'
        }
    ]
}
